import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export const UniUniShipmentStatus = {
  // Order Creation
  DRAFT: "DRAFT",
  PENDING: "PENDING",
  PENDING_REVIEW: "PENDING_REVIEW",
  CANCELLED: "CANCELLED",

  // First Mile
  PICKUP_REQUESTED: "PICKUP_REQUESTED",
  PICKUP_CANCELLED: "PICKUP_CANCELLED",
  PARTNER_RECEIVED: "PARTNER_RECEIVED",
  PICKED_UP: "PICKED_UP",

  // Middle Mile
  RECEIVED: "RECEIVED",

  // Last Mile — Transit
  GATEWAY_TRANSIT_OUT: "GATEWAY_TRANSIT_OUT",
  GATEWAY_TO_GATEWAY_TRANSIT: "GATEWAY_TO_GATEWAY_TRANSIT",
  GATEWAY_TRANSIT: "GATEWAY_TRANSIT",
  PARCEL_SCANNED: "PARCEL_SCANNED",
  OUT_FOR_DELIVERY: "OUT_FOR_DELIVERY",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",

  // Last Mile — Exceptions
  SHIPMENT_EXCEPTION: "SHIPMENT_EXCEPTION",
  TRANSSHIPMENT_COMPLETE: "TRANSSHIPMENT_COMPLETE",
  RETURNED: "RETURNED",
  FAILED: "FAILED",
  FAILED_DELIVERY1: "FAILED_DELIVERY1",
  FAILED_DELIVERY2: "FAILED_DELIVERY2",
  STORAGE: "STORAGE",
} as const;
export type UniUniShipmentStatus =
  (typeof UniUniShipmentStatus)[keyof typeof UniUniShipmentStatus];

export const uniUniShipmentStatusMessages: Record<
  UniUniShipmentStatus,
  string
> = {
  DRAFT: "Shipment created",
  PENDING: "Shipment pending pickup",
  PENDING_REVIEW: "Shipment requires manual review",
  CANCELLED: "Shipment cancelled",
  PICKUP_REQUESTED: "Pickup requested",
  PICKUP_CANCELLED: "Pickup cancelled",
  PARTNER_RECEIVED: "Package received by partner",
  PICKED_UP: "Package picked up",
  RECEIVED: "Package received at facility",
  GATEWAY_TRANSIT_OUT: "Package departed gateway",
  GATEWAY_TO_GATEWAY_TRANSIT: "Package in gateway-to-gateway transit",
  GATEWAY_TRANSIT: "Package in gateway transit",
  PARCEL_SCANNED: "Parcel scanned",
  OUT_FOR_DELIVERY: "Package out for delivery",
  IN_TRANSIT: "Package in transit",
  DELIVERED: "Package delivered",
  SHIPMENT_EXCEPTION: "Shipment exception",
  TRANSSHIPMENT_COMPLETE: "Transshipment complete",
  RETURNED: "Package returned to sender",
  FAILED: "Shipment failed",
  FAILED_DELIVERY1: "First delivery attempt failed",
  FAILED_DELIVERY2: "Second delivery attempt failed",
  STORAGE: "Package in storage",
};

const UniUniWebhookAddressSchema_ = z.object({
  address1: z.string(),
  address2: z.string().optional(),
  address3: z.string().optional(),
  city: z.string(),
  province: z.string(),
  postalCode: z.string(),
  country: z.string(),
  latitude: z.union([z.string(), z.number()]).optional(),
  longitude: z.union([z.string(), z.number()]).optional(),
});
export const UniUniWebhookAddressSchema: z.ZodType<UniUniWebhookAddress> =
  UniUniWebhookAddressSchema_;
export interface UniUniWebhookAddress
  extends z.infer<typeof UniUniWebhookAddressSchema_> {}

const UniUniProofOfDeliverySchema_ = z.object({
  trackingNumber: z.string(),
  recipient: z.string(),
  deliveryDate: z.string(),
  deliveryTime: z.string(),
  pods: z.array(z.string()),
});
export const UniUniProofOfDeliverySchema: z.ZodType<UniUniProofOfDelivery> =
  UniUniProofOfDeliverySchema_;
export interface UniUniProofOfDelivery
  extends z.infer<typeof UniUniProofOfDeliverySchema_> {}

const UniUniWebhookDataSchema_ = z.object({
  objectType: z.string().optional(),
  trackingId: z.string(),
  status: createEnum(UniUniShipmentStatus),
  statusCode: z.number(),
  updatedAt: z.string(),
  transferCarrierName: z.string().optional(),
  transferCarrierTrackingId: z.string().optional(),
  address: UniUniWebhookAddressSchema.optional(),
  proofOfDelivery: UniUniProofOfDeliverySchema.optional(),
});
export const UniUniWebhookDataSchema: z.ZodType<UniUniWebhookData> =
  UniUniWebhookDataSchema_;
export interface UniUniWebhookData
  extends z.infer<typeof UniUniWebhookDataSchema_> {}

export const uniUniWebhookEvents = ["shipment.status_updated"] as const;
export type UniUniWebhookEvent = (typeof uniUniWebhookEvents)[number];

const UniUniWebhookPayloadSchema_ = z.object({
  event: z.enum(uniUniWebhookEvents),
  version: z.string(),
  data: UniUniWebhookDataSchema,
});
export const UniUniWebhookPayloadSchema: z.ZodType<UniUniWebhookPayload> =
  UniUniWebhookPayloadSchema_;
export interface UniUniWebhookPayload
  extends z.infer<typeof UniUniWebhookPayloadSchema_> {}
